import { FormInstance } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import useNotification from "utils/hooks/useNotification";
import useEmailAttachmentsApi, { EmailExtraFile } from "./useEmailAttachmentsApi";

export default function useEmailExtraFileCrud(form: FormInstance) {
  const { t } = useTranslation();
  const urlParams: RouteParams = useParams();
  const { notificationSuccessfullySaved, notificationError } = useNotification();

  const {
    extraFiles,
    listEmailExtraFiles,
    listLoading,
    refetch,
    createEmailExtraFile,
    createLoading,
    updateEmailExtraFile,
    updateLoading,
    deleteEmailExtraFile,
    deleteLoading,
  } = useEmailAttachmentsApi();

  useEffect(() => {
    listEmailExtraFiles({
      variables: { filter: { companyId: urlParams?.companyId } },
    });
  }, []);

  const handleCreate = async () => {
    const values = await form.validateFields();
    const fileValue = values.file as { file?: { originFileObj?: File; name?: string } } | undefined;
    const file = (fileValue?.file?.originFileObj || fileValue?.file) as File | undefined;

    if (!file) return;

    const response = await createEmailExtraFile({
      variables: {
        input: {
          companyId: urlParams.companyId,
          file,
          label: (values.label as string) || fileValue?.file?.name || "",
          isActiveBudget: values.isActiveBudget || false,
          isActiveSaleOrder: values.isActiveSaleOrder || false,
          isActiveDeliveryForm: values.isActiveDeliveryForm || false,
          isActivePurchaseOrder: values.isActivePurchaseOrder || false,
          isActiveReturn: values.isActiveReturn || false,
          isActiveReceipt: values.isActiveReceipt || false,
          isActivePaymentInAdvance: values.isActivePaymentInAdvance || false,
        },
      },
    });

    const errors = response?.data?.createEmailExtraFile?.errors;
    if (errors && errors.length > 0) {
      notificationError(errors);
    } else {
      notificationSuccessfullySaved(t("Attachment"));
      form.resetFields();
      refetch();
    }
  };

  const handleUpdate = async (item: EmailExtraFile, values: Partial<EmailExtraFile>) => {
    const response = await updateEmailExtraFile({
      variables: {
        input: {
          companyId: urlParams.companyId,
          id: item.id,
          label: item.label,
          isActiveBudget: item.isActiveBudget,
          isActiveSaleOrder: item.isActiveSaleOrder,
          isActiveDeliveryForm: item.isActiveDeliveryForm,
          isActivePurchaseOrder: item.isActivePurchaseOrder,
          isActiveReturn: item.isActiveReturn,
          isActiveReceipt: item.isActiveReceipt,
          isActivePaymentInAdvance: item.isActivePaymentInAdvance,
          ...values,
        },
      },
    });

    const errors = response?.data?.updateEmailExtraFile?.errors;
    if (errors && errors.length > 0) {
      notificationError(errors);
    } else {
      refetch();
    }
  };

  const handleDelete = async (item: EmailExtraFile) => {
    const response = await deleteEmailExtraFile({
      variables: { id: item.id },
    });

    const errors = response?.data?.deleteEmailExtraFile?.errors;
    if (errors && errors.length > 0) {
      notificationError(errors);
    } else {
      notificationSuccessfullySaved(t("Attachment deleted"));
      refetch();
    }
  };

  return {
    extraFiles,
    loading: listLoading || createLoading,
    createLoading,
    mutationLoading: updateLoading || deleteLoading,
    handleCreate,
    handleUpdate,
    handleDelete,
  };
}
